import { Component, OnInit, HostBinding, EventEmitter, Output, Input, ViewChild, TemplateRef, Injector, ComponentFactoryResolver, Optional, ChangeDetectorRef, Inject, LOCALE_ID } from '@angular/core';
import { TypeConverter } from '@farris/ide-property-panel';
import { ProcessVariable } from './process-variable.entity';
import { EditorTypes } from '@farris/ui-datagrid-editors';
import { DatagridComponent } from '@farris/ui-datagrid';
import { MessagerService } from '@farris/ui-messager';
import { BsModalService } from '@farris/ui-modal';
import { ConstantEditComponent } from '../variable-setting/views/constant-edit/constant-edit.component';
import { ProcessDesignerUIState } from '../../app/process-designer.uistate';
import { ExprEditMode, ExpressionService, InjectedType } from '@gsp-svc/expression';
import { ComponentEditComponent } from '../variable-setting/views/component-edit/component-edit.component';
import { RowEditParam, DataColumn } from '@farris/ui-datagrid/lib/types';
import { of } from 'rxjs';
import { VariableSetting } from '../variable-setting/variable-setting';
import { WF_LANG_RESOURCES, WfLocalePipe } from '../../i18n/pipe/wf-process-editor-locale.pipe';
import { WF_PROCESS_EDITOR_LANG_RESOURCES } from '../../i18n/assets/lang.resource';

@Component({
  selector: 'lib-process-variables',
  templateUrl: './process-variables.component.html',
  styleUrls: ['./process-variables.component.scss'],
  providers: [
     WfLocalePipe,
  ]
})
export class ProcessVariablesComponent implements OnInit {
  @HostBinding('class')
  cls = 'farris-main-area flex-column';

  @Input() variableSetting = false;
  @Output() closeModal = new EventEmitter<any>();
  @Output() submitModal = new EventEmitter<any>();
  @Output() variableSetEmitter = new EventEmitter<any>();

  @Input() value: Array<ProcessVariable> = new Array();
  @Input() selectedValue: Array<VariableSetting> = new Array();
  @Input() editorParams: any = {};
  @ViewChild('iconSelectFooter') modalFooter: TemplateRef<any>;
  @ViewChild('grid') grid: DatagridComponent;

  private basicTypes = [
    { value: 'String', text: this.localePipe.transform('component.processVariables.string') },
    { value: 'Integer', text: this.localePipe.transform('component.processVariables.integer') },
    { value: 'Decimal', text: this.localePipe.transform('component.processVariables.decimal') },
    { value: 'Boolean', text: this.localePipe.transform('component.processVariables.boolean') },
    // { value: 'DateTime', text: '日期' }
  ];

  private tags = [
    { value: 'constant', text: this.localePipe.transform('component.processVariables.constant') },
    { value: 'expression', text: this.localePipe.transform('component.processVariables.expression') },
    { value: 'component', text: this.localePipe.transform('component.processVariables.component') }
  ];

  columns = [
    {
      field: 'name', width: 80, title: this.localePipe.transform('component.processVariables.name'), sortable: true,
      editor: { type: EditorTypes.TEXTBOX, options: {} },
      validators: [
        { type: 'required', messager: '必填' }
      ]
    },
    {
      field: 'documentation', width: 80, title: this.localePipe.transform('component.processVariables.documentation'), sortable: true,
      editor: { type: EditorTypes.TEXTBOX, options: {} },
      validators: [
        { type: 'required', messager: '必填' }
      ]
    },
    {
      field: 'basicType', width: 60, title: this.localePipe.transform('component.processVariables.basicType'), sortable: true,
      formatter: {
        type: 'enum',
        options: { valueField: 'value', textField: 'text', data: this.basicTypes }
      },
      editor: {
        type: EditorTypes.COMBOLIST,
        options: { valueField: 'value', textField: 'text', data: this.basicTypes, idField: 'value', panelHeight: 'auto', showClear: false }
      },
      validators: [
        { type: 'required', messager: '必填' }
      ]
    }, {
      field: 'description', width: 100, title: this.localePipe.transform('component.processVariables.description'), showTips: true,
      editor: { type: EditorTypes.TEXTBOX, options: {} },
    }, {
      field: 'tag', width: 60, title: this.localePipe.transform('component.processVariables.tag'), sortable: true,
      formatter: {
        type: 'enum',
        options: { valueField: 'value', textField: 'text', data: this.tags }
      },
      editor: {
        type: EditorTypes.COMBOLIST,
        options: {
          valueField: 'value', textField: 'text', data: this.tags, idField: 'value', panelHeight: 'auto',
          selectChange: (e) => {
            this.value = this.value.map(v => {
              if (v.id === e.rowId) {
                v.value = null;
                v.valueText = null;
                if (v.component) {
                  v.component = null;
                }
                return v;
              } else {
                return v;
              }
            });
          },
          clear: (e) => {
            this.value = this.value.map(v => {
              if (v.id === e.rowId) {
                v.value = null;
                v.valueText = null;
                if (v.component) {
                  v.component = null;
                }
                return v;
              } else {
                return v;
              }
            });
          }
        }
      }
    }, {
      field: 'valueText', width: 120, title: this.localePipe.transform('component.processVariables.valueText'), showTips: true,
      editor: { type: EditorTypes.TEXTBOX, options: {} }
    }
  ];

  modalConfig = {
    title: this.localePipe.transform('component.processVariables.processVariables'),
    width: 1070,
    height: 510,
    showButtons: true
  };

  constructor(
    private message: MessagerService,
    private resolver: ComponentFactoryResolver,
    private injector: Injector,
    private modalService: BsModalService,
    private uistate: ProcessDesignerUIState,
    private expr: ExpressionService,
    @Optional() private cdr: ChangeDetectorRef,
    @Inject(LOCALE_ID) private localeId: string,
    @Inject(WF_LANG_RESOURCES) private resources: any,
    @Optional() private localePipe: WfLocalePipe
  ) {
    this.localePipe = this.localePipe || new WfLocalePipe(localeId, resources);
  }

  ngOnInit() {
    if (this.variableSetting) {
      this.columns = this.columns.filter(c => c.field !== 'tag'
        && c.field !== 'valueText' && c.field !== 'description'
      );
    }
  }

  add() {
    const variable = new ProcessVariable();
    this.value = this.value.concat([variable]);
  }

  delete() {
    this.grid.endCellEdit();
    const selected = this.grid.selectedRow;
    if (!selected) {
      this.message.info(this.localePipe.transform('component.processVariables.notSelected'));
      return;
    }
    this.value = this.value.filter(v => v.id !== selected.data.id);
  }

  clickOK() {
    if (this.variableSetting) {
      const selecteds = this.grid.checkeds;
      if (selecteds.length > 0 ) {
        if (this.varValidator()) {
          this.variableSetEmitter.emit(selecteds);
        }
      } else {
        this.message.info(this.localePipe.transform('component.processVariables.notSelected'));
      }
    } else {
      this.grid.endCellEdit();
      if (this.validator()) {
        const object = { value: this.value, parameters: null };
        this.submitModal.emit(object);
      }
    }
  }

  clickCancel() {
    this.closeModal.emit();
  }
  beforeEdit = (param: RowEditParam) => {
    const rowData = param.rowData as any;
    const column = param.column;
    if (column.field === 'valueText') {
      if (!(rowData as ProcessVariable).tag) {
        return of(false);
      }
      if ((rowData as ProcessVariable).tag === this.tags[0].value) {
        param.column.editor = { type: EditorTypes.TEXTBOX, options: {} };
      } else {
        param.column.editor = {
          type: 'input-group',
          options: {
            groupText: '<i class="f-icon f-icon-lookup"></i>',
            editable: false,
            showClear: false,
            clickHandle: (e) => {
              e.formControl.setValue(e.instance.value ? e.instance.value : '');
              this.valueEdit(e);
            },
            clear: (e) => {
              console.log('clear', e);
            }
          }
        };
      }
    }
    return of(true);
  }

  endEdit(data: any) {
    if (data.column.field === 'valueText' && data.rowData.tag === this.tags[0].value) {
      data.rowData.value = data.rowData.valueText;
    }
  }
  valueEdit(e: any) {
    const rowdata: ProcessVariable = e.rowData;
    switch (rowdata.tag) {
      case this.tags[0].value:
        this.editConstant(e);
        break;
      case this.tags[1].value:
        this.editExpression(e);
        break;
      case this.tags[2].value:
        this.editComponent(e);
        break;
    }
  }
  private editConstant(e: any) {
    const data: ProcessVariable = e.rowData;
    let dialog: any;
    const compFactory = this.resolver.resolveComponentFactory(ConstantEditComponent);
    const compRef = compFactory.create(this.injector);
    compRef.instance.value = data.value;
    compRef.instance.submitEmitter.subscribe((re: string) => {
      e.formControl.setValue(re);
      e.instance.cd.detectChanges();
      data.value = re;
      dialog.close();
    });
    compRef.instance.closeEmitter.subscribe(re => {
      dialog.close();
    });
    const modalConfig = {
      title: this.localePipe.transform('component.processVariables.constant'),
      width: 340,
      height: 280,
      showButtons: true,
      buttons: compRef.instance.footer
    };
    dialog = this.modalService.show(compRef, modalConfig);
  }

  private editExpression(e: any) {
    // this.expr.clearContext();
    const data: ProcessVariable = e.rowData;
    if (this.uistate.schemas.length > 0) {
      for (const schema of this.uistate.schemas) {
        this.expr.addSchema('Schema', schema);
      }
      if (this.uistate.schemas[0].entityTypes && this.uistate.schemas[0].entityTypes.length > 0) {
        const entityType = this.uistate.schemas[0].entityTypes[0];
        this.expr.addInjectedEntity(entityType.name, 'Schema',
          entityType.name, InjectedType.EntityType);
      }
    }
    let context = new Array();
    context = this.uistate.addContext(context);
    if (this.uistate.subActivityContext.length > 0) {
      for (const variable of this.uistate.subActivityContext) {
        if (!context.some(c => c.key === variable.key)) {
          context.push(variable);
        }
      }
    }
    if (context.length > 0) {
      for (const v of context) {
        this.expr.addContext(v);
      }
    }
    this.expr.setEditPattern(ExprEditMode.Advanced); // 表达式展现模式：默认高级版
    this.expr.addExpressionText(data.value);
    this.expr.buildExpression().then((re: string) => {
      e.formControl.setValue(re);
      e.instance.cd.detectChanges();
      data.value = re;
      e.form.bindingData.value = re;
      e.form.value.value = re;
      //点击确定后，去除表达式里面的新增的上下文变量
      if(context.length > 0) {
        this.expr.exprEntity.contextEntities = this.expr.exprEntity.contextEntities.filter(x => !context.some(y => y.key === x.key));
      }
    });
  }

  private editComponent(e: any) {
    const data: ProcessVariable = e.rowData;
    let dialog: any;
    const compFactory = this.resolver.resolveComponentFactory(ComponentEditComponent);
    const compRef = compFactory.create(this.injector);
    compRef.instance.component = data.component;
    compRef.instance.submitEmitter.subscribe((re: any) => {
      e.formControl.setValue(re.Name + '-' + re.MethodCode);
      e.instance.cd.detectChanges();
      data.value = re.Id;
      data.component = re;
      e.form.bindingData.value = re.Id;
      e.form.value.value = re.Id;
      e.form.value.component = re;
      e.form.bindingData.component = re;
      dialog.close();
    });
    compRef.instance.closeEmitter.subscribe(re => {
      dialog.close();
    });
    const modalConfig = {
      title: this.localePipe.transform('component.processVariables.component'),
      width: 800,
      height: 515,
      showButtons: true,
      buttons: compRef.instance.footer
    };
    dialog = this.modalService.show(compRef, modalConfig);
  }

  private validator() {
    for (const item of this.value) {
      if (!item.name) {
        this.message.warning(this.localePipe.transform('component.processVariables.variableNumberEmpty'));
        return false;
      }
      if (!item.documentation) {
        this.message.warning(this.localePipe.transform('component.processVariables.variableNameEmpty'));
        return false;
      }
      if (!this.IsMatchVariableNameRules(item.name)) {
        this.message.warning(this.localePipe.transform('component.processVariables.variableNumberNotStandard'));
        return false;
      }
      if (item.value && !item.tag) {
        this.message.warning(this.localePipe.transform('component.processVariables.tagNotEmpty'));
        return false;
      }
    }
    if (this.value.length > 1) {
      for (let i = 0; i < this.value.length; i++) {
        const code1 = this.value[i].name;
        for (let j = i + 1; j < this.value.length; j++) {
          const code2 = this.value[j].name;

          if (code1 === code2) {
            this.message.warning(this.localePipe.transform('component.processVariables.codeDuplicates'));
            return false;
          }
        }
      }
    }
    return true;
  }
  private IsMatchVariableNameRules(str: string) {
    const Regx = /^[a-zA-Z_][a-zA-Z_0-9]{0,}/;
    if (Regx.test(str)) {
      return true;
    } else {
      return false;
    }
  }

  // 变量赋值专用校验
  private varValidator() {
    for (const item of this.grid.checkeds) {
      if (this.selectedValue.length && this.selectedValue.length > 0) {
        if (this.selectedValue.filter(v => v.variableCode === item.data.name).length > 0){
          this.message.warning(this.localePipe.transform('component.processVariables.alreadyExists'));
          return false;
        }
      }
    }
    return true;
  }
}

export class ProcessVariablesConverter implements TypeConverter {
  constructor(
    private localePipe: WfLocalePipe
  ) { }

  convertTo(data: any[]): string {
    if (data && data.length > 0) {
      return this.localePipe.transform("component.processVariables.set");
    } else {
      return this.localePipe.transform("component.processVariables.notSet");
    }
  }
}
